/**
 * Infinite scroll for the blog listing (amb-blogs-filter block).
 *
 * Behaviour:
 * - Loads the next page when the sentinel enters the viewport via the
 *   Section Rendering API.
 * - Never pauses for filters: if the active filter hides most cards, the
 *   sentinel stays in view, which auto-loads further pages until matching
 *   articles surface or the blog is exhausted. This is what fixes the
 *   "tag filter shows nothing for tags whose articles are on later pages"
 *   bug.
 * - On article-click / page-leave, the current scroll Y and number of
 *   loaded cards are saved to localStorage keyed by URL. On return to the
 *   same URL (browser Back without bfcache), pages are loaded back in
 *   order and the previous scroll position is smoothly restored.
 */
const SECTION_ID_PREFIX = 'shopify-section-';
const SCROLL_STORAGE_PREFIX = 'blogScroll:';
const SCROLL_TTL_MS = 30 * 60 * 1000; // 30 minutes
const DEFAULT_ARTICLES_PER_PAGE = 9; // matches the block's articles_limit default

function buildSectionUrl(sectionId, page) {
  const url = new URL(window.location.href);
  url.searchParams.set('section_id', sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), ''));
  url.searchParams.set('page', String(page));
  url.hash = '';
  return url.toString();
}

function buildDisplayUrl(page) {
  const url = new URL(window.location.href);
  if (page <= 1) {
    url.searchParams.delete('page');
  } else {
    url.searchParams.set('page', String(page));
  }
  url.searchParams.delete('section_id');
  url.hash = '';
  return url.pathname + (url.search || '');
}

// Storage key derived from pathname + sorted, normalised query params
// (page/section_id excluded so a saved state survives infinite-scroll URL bumps).
function scrollKey() {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  params.delete('page');
  params.delete('section_id');
  const entries = [...params.entries()].sort(([a], [b]) => a.localeCompare(b));
  const search = entries.length
    ? '?' + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&')
    : '';
  return SCROLL_STORAGE_PREFIX + url.pathname + search;
}

function saveScrollState(grid) {
  try {
    const cardsLoaded = grid.querySelectorAll('.blog-article-card').length;
    if (cardsLoaded === 0) return;
    const data = { y: window.scrollY, cardsLoaded, ts: Date.now() };
    localStorage.setItem(scrollKey(), JSON.stringify(data));
  } catch {
    // localStorage may be unavailable (private mode, quota); silently skip.
  }
}

function readScrollState() {
  try {
    const raw = localStorage.getItem(scrollKey());
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data.y !== 'number') return null;
    if (Date.now() - (data.ts || 0) > SCROLL_TTL_MS) {
      localStorage.removeItem(scrollKey());
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function clearScrollState() {
  try {
    localStorage.removeItem(scrollKey());
  } catch {
    /* noop */
  }
}

// Number of cards rendered for page 1 (per request). Falls back to the
// first available data-page chunk, then to a sensible default.
function getArticlesPerPage(grid) {
  const firstPage = grid.querySelectorAll('.blog-article-card[data-page="1"]').length;
  if (firstPage > 0) return firstPage;
  const anyPagedCard = grid.querySelector('.blog-article-card[data-page]');
  if (anyPagedCard) {
    const pageVal = anyPagedCard.dataset.page;
    return grid.querySelectorAll(`.blog-article-card[data-page="${pageVal}"]`).length || DEFAULT_ARTICLES_PER_PAGE;
  }
  return DEFAULT_ARTICLES_PER_PAGE;
}

function initBlogListingInfiniteScroll() {
  const grid = document.querySelector('[data-blog-listing-grid][data-section-id]');
  if (!grid) return;

  const sectionId = grid.dataset.sectionId;
  const lastPage = parseInt(grid.dataset.lastPage, 10);
  const sentinel = grid.querySelector('[data-blog-listing-sentinel]');
  const loader = grid.querySelector('[data-blog-listing-loader]');
  if (!sectionId || !lastPage || !sentinel) return;

  let loading = false;

  function setLoaderVisible(visible) {
    if (!loader) return;
    loader.classList.toggle('blog-listing-loader--visible', visible);
    loader.setAttribute('aria-hidden', String(!visible));
  }

  function getCurrentPage() {
    const cards = grid.querySelectorAll('.blog-article-card[data-page]');
    if (cards.length === 0) return 1;
    const lastCard = cards[cards.length - 1];
    return parseInt(lastCard.dataset.page, 10) || 1;
  }

  async function fetchPageCards(page) {
    const url = buildSectionUrl(sectionId, page);
    const response = await fetch(url);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const sectionEl = doc.getElementById(`${SECTION_ID_PREFIX}${sectionId}`);
    if (!sectionEl) return [];
    const responseGrid = sectionEl.querySelector('[data-blog-listing-grid]');
    if (!responseGrid) return [];
    return Array.from(responseGrid.querySelectorAll('.blog-article-card'));
  }

  // Loads (currentPage + 1) and appends. Returns true if cards were added.
  async function appendNextPage() {
    const currentPage = getCurrentPage();
    if (currentPage >= lastPage || loading) return false;

    loading = true;
    setLoaderVisible(true);
    const nextPage = currentPage + 1;
    try {
      const newCards = await fetchPageCards(nextPage);
      if (newCards.length === 0) return false;

      sentinel.before(...newCards);

      const filterBlock = grid.closest('.blog-filter-block');
      if (filterBlock && typeof filterBlock.updateArticles === 'function') {
        filterBlock.updateArticles();
        if (typeof filterBlock.filterArticles === 'function') {
          filterBlock.filterArticles({ silent: true });
        }
      }

      history.replaceState(null, '', buildDisplayUrl(nextPage));
      return true;
    } finally {
      loading = false;
      setLoaderVisible(false);
    }
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) appendNextPage();
      }
    },
    { rootMargin: '200px', threshold: 0 }
  );
  observer.observe(sentinel);

  // ---- Save scroll state on leave (covers click + tab-close + back-nav) ----
  const onPageHide = () => saveScrollState(grid);
  window.addEventListener('pagehide', onPageHide);
  // pagehide is enough on modern browsers; visibilitychange as belt-and-braces
  // for cases where the browser skips pagehide on certain platforms.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') saveScrollState(grid);
  });

  // ---- On init: optionally restore scroll OR load up to ?page=N ----
  async function restoreOnInit() {
    const params = new URLSearchParams(window.location.search);
    const pageFromUrl = parseInt(params.get('page'), 10);
    const savedScroll = readScrollState();

    let targetPage = Number.isFinite(pageFromUrl) && pageFromUrl > 1 ? pageFromUrl : 1;
    let targetCards = savedScroll ? savedScroll.cardsLoaded : 0;

    if (targetCards > 0) {
      const perPage = getArticlesPerPage(grid);
      const pagesNeeded = Math.max(1, Math.ceil(targetCards / perPage));
      targetPage = Math.max(targetPage, pagesNeeded);
    }

    // Nothing to do.
    if (targetPage <= 1 && targetCards === 0) return;

    // Pull pages sequentially until we hit the target (or run out).
    while (true) {
      const currentPage = getCurrentPage();
      const haveCards = grid.querySelectorAll('.blog-article-card').length;
      const enoughCards = targetCards > 0 ? haveCards >= targetCards : true;
      const enoughPages = currentPage >= targetPage;
      if ((enoughPages && enoughCards) || currentPage >= lastPage) break;
      const ok = await appendNextPage();
      if (!ok) break;
    }

    if (savedScroll) {
      // Wait one frame so freshly-inserted cards are laid out.
      requestAnimationFrame(() => {
        window.scrollTo({ top: savedScroll.y, behavior: 'smooth' });
      });
      clearScrollState();
    } else if (targetPage > 1) {
      const firstCardOfRequestedPage = grid.querySelector(
        `.blog-article-card[data-page="${pageFromUrl}"]`
      );
      if (firstCardOfRequestedPage) {
        firstCardOfRequestedPage.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  restoreOnInit();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBlogListingInfiniteScroll);
} else {
  initBlogListingInfiniteScroll();
}
